'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SongArtwork } from '@/components/ui/song-artwork'
import { DollarSign, Info } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { InlineSheet } from './inline-sheet'
import { MockPaymentSheet } from '../mock-payment-sheet'
import type { DemoQueueRequest, DemoEventSettings } from '../demo-types'

// ─── Direct Tip Sheet ─────────────────────────────────────────────────────

interface TipSheetProps {
  open: boolean
  onClose: () => void
  settings: DemoEventSettings
  artistName: string
  onSendTip: (tipCents: number) => void
}

export function TipSheet({ open, onClose, settings, artistName, onSendTip }: TipSheetProps) {
  const t = useTranslations('demo')
  const [selectedTipAmount, setSelectedTipAmount] = useState(500)
  const [showPayment, setShowPayment] = useState(false)

  const handleClose = () => {
    setShowPayment(false)
    onClose()
  }

  return (
    <InlineSheet
      open={open}
      onClose={handleClose}
      title={t('sendATip')}
      description={t('showAppreciation', { artistName })}
    >
      {showPayment ? (
        <MockPaymentSheet
          amount={selectedTipAmount}
          onSuccess={() => {
            onSendTip(selectedTipAmount)
            setShowPayment(false)
          }}
          onCancel={() => setShowPayment(false)}
          description={t('tipArtist', { artistName })}
          inline
        />
      ) : (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {settings.tipAmounts.map(amount => (
              <Button
                key={amount}
                variant={selectedTipAmount === amount ? 'default' : 'outline'}
                className={selectedTipAmount === amount
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 border-0'
                  : 'border-zinc-700'}
                onClick={() => setSelectedTipAmount(amount)}
              >
                ${(amount / 100).toFixed(0)}
              </Button>
            ))}
          </div>

          <Button
            className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            onClick={() => {
              setShowPayment(true)
            }}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            {t('sendTipAmount', { amount: (selectedTipAmount / 100).toFixed(0) })}
          </Button>
        </div>
      )}
    </InlineSheet>
  )
}

// ─── Boost Sheet ──────────────────────────────────────────────────────────

interface BoostSheetProps {
  open: boolean
  onClose: () => void
  settings: DemoEventSettings
  boostingRequest: DemoQueueRequest | null
  onBoost: (request: DemoQueueRequest, tipCents: number) => void
}

export function BoostSheet({ open, onClose, settings, boostingRequest, onBoost }: BoostSheetProps) {
  const t = useTranslations('demo')
  const [selectedTipAmount, setSelectedTipAmount] = useState(500)
  const [showPayment, setShowPayment] = useState(false)

  const handleClose = () => {
    setShowPayment(false)
    onClose()
  }

  if (!boostingRequest) return null

  return (
    <InlineSheet
      open={open}
      onClose={handleClose}
      title={t('boostRequest')}
      description={t('boostOnlyChargedIfPlayed')}
    >
      {showPayment ? (
        <MockPaymentSheet
          amount={selectedTipAmount}
          onSuccess={() => {
            onBoost(boostingRequest, selectedTipAmount)
            setShowPayment(false)
          }}
          onCancel={() => setShowPayment(false)}
          description={t('boostDescription', { songTitle: boostingRequest.song.title })}
          inline
        />
      ) : (
        <div className="p-4 space-y-4">
          {/* Conditional tip notice */}
          <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <Info className="h-4 w-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-300">
              {t.rich('boostConditionalNotice', { strong: (chunks) => <strong>{chunks}</strong> })}
            </p>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
            <SongArtwork
              src={boostingRequest.song.artworkUrl}
              alt={boostingRequest.song.title}
              size={48}
              className="h-12 w-12 rounded shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white">{boostingRequest.song.title}</p>
              <p className="text-sm text-zinc-400">{boostingRequest.song.artist}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {boostingRequest.totalTipsCents > 0 && (
                  <Badge variant="outline" className="text-xs border-green-500/30 text-green-400">
                    {t('totalBoost', { amount: (boostingRequest.totalTipsCents / 100).toFixed(0) })}
                  </Badge>
                )}
                {boostingRequest.visitorBoostCents > 0 && (
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    {t('yourBoostAmount', { amount: (boostingRequest.visitorBoostCents / 100).toFixed(0) })}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {settings.tipAmounts.map(amount => (
              <Button
                key={amount}
                variant={selectedTipAmount === amount ? 'default' : 'outline'}
                className={selectedTipAmount === amount
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 border-0'
                  : 'border-zinc-700'}
                onClick={() => setSelectedTipAmount(amount)}
              >
                +${(amount / 100).toFixed(0)}
              </Button>
            ))}
          </div>

          <Button
            className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            onClick={() => {
              setShowPayment(true)
            }}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            {boostingRequest.visitorBoostCents > 0
              ? t('addMore', { amount: (selectedTipAmount / 100).toFixed(0) })
              : t('addBoost', { amount: (selectedTipAmount / 100).toFixed(0) })}
          </Button>
        </div>
      )}
    </InlineSheet>
  )
}
